import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const gallerySections = [
  {
    id: "studio",
    title: "Recording Studio",
    description: "Professional recording environment with state-of-the-art equipment"
  },
  {
    id: "dj",
    title: "Dream Makers Sessions",
    description: "Featuring Pioneer CDJ3000s & DJM A9"
  },
  {
    id: "production",
    title: "Production Room",
    description: "Your private creative space with premium monitors and acoustic treatment"
  },
  {
    id: "photography",
    title: "Photography Studio",
    description: "Capture your artistic vision with professional lighting and backdrops"
  },
  {
    id: "lounge",
    title: "Dream Makers Lounge Area",
    description: "Relax before and after sessions, play PS5, with free water and soft drinks included in all bookings"
  }
];

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setImages(res.data.images);
      } catch (error) {
        console.error("Error fetching gallery:", error);
        // Fallback images
        setImages([
          { id: "studio0", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/gt8mj61f_DSC_0100.jpg", alt: "Control Room - Main View", category: "studio" },
          { id: "studio1", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/wdkege0p_DSC_0076.jpg", alt: "Control Room - Mixing Console & Focal Monitors", category: "studio" },
          { id: "studio2", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/0epweuqa_DSC_0085.jpg", alt: "Recording Booth - Professional Microphone", category: "studio" },
          { id: "studio3", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/p2m69fbe_DSC_0086.jpg", alt: "Live Room - Drum Kit", category: "studio" },
          { id: "studio4", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/nx4ovlaw_DSC_0087.jpg", alt: "Live Room - Full View", category: "studio" },
          { id: "dj1", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/o4aghb0q_DSC_2699.jpg", alt: "DJ Room Decks", category: "dj" },
          { id: "dj2", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/9c2fw01q_DSC_2674.jpg", alt: "DJ Setup", category: "dj" },
          { id: "dj3", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/irjscrt2_DSC_0145.jpg", alt: "DJ Room Lounge", category: "dj" },
          { id: "dj4", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/5hedikfg_DSC_2648.jpg", alt: "DJ Room Equipment", category: "dj" },
          { id: "dj5", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/r9ezf2ho_DSC_2727.jpg", alt: "DJ Room View", category: "dj" },
          { id: "prod1", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/unb0ag3c_Untitled%20design-3.jpg", alt: "Production Room - Main View", category: "production" },
          { id: "prod2", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/2b5e40g6_DSC_0111.jpg", alt: "Production Workstation", category: "production" },
          { id: "prod3", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/9000lt8p_DSC_0119.jpg", alt: "Production Setup", category: "production" },
          { id: "prod4", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/y2hb9oi1_DSC_0130.jpg", alt: "Acoustic Treatment", category: "production" },
          { id: "prod5", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/hu3nk4lc_DSC_0152.jpg", alt: "Production Room Full View", category: "production" },
          { id: "photo1", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/npv3htf0_DSC_6852.jpg", alt: "Photography Studio Setup", category: "photography" },
          { id: "photo2", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/cqhva9gl_DSC_0489.jpg", alt: "Artist Press Shot", category: "photography" },
          { id: "photo3", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/gsvvq2rw_DSC_1522.jpg", alt: "Artist Portrait", category: "photography" },
          { id: "photo4", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/hiffqvbe_DSC_0004.jpg", alt: "Artist Session", category: "photography" },
          { id: "photo5", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/d4wllc08_DSC_2596.jpg", alt: "Press Shot", category: "photography" },
          { id: "lounge1", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/5rv01svi_DSC_0132.jpg", alt: "Lounge Area", category: "lounge" },
          { id: "lounge2", url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/aohwp4p8_DSC_0140.jpg", alt: "Lounge with PS5", category: "lounge" },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const getImagesByCategory = (category) => {
    return images.filter(img => img.category === category);
  };

  return (
    <div className="pt-20" data-testid="gallery-page">
      {/* Hero */}
      <section className="py-24 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Gallery</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Our Studio Spaces
            </h1>
            <p className="text-white/60 text-lg">
              Take a look inside Dream Makers Recording Group. Premium equipment, 
              professional acoustics, and inspiring creative spaces.
            </p>
          </motion.div>
        </div>
      </section>

      {loading ? (
        <div className="text-center py-16 bg-dream-surface">
          <div className="w-12 h-12 border-4 border-dream-violet border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : (
        gallerySections.map((section, sectionIndex) => {
          const sectionImages = getImagesByCategory(section.id);
          if (sectionImages.length === 0) return null;
          
          return (
            <section 
              key={section.id} 
              className={`py-16 ${sectionIndex % 2 === 0 ? 'bg-dream-surface' : 'bg-dream-bg'}`}
              data-testid={`gallery-section-${section.id}`}
            >
              <div className="max-w-7xl mx-auto px-6">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  className="mb-10"
                >
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                    {section.title}
                  </h2>
                  <p className="text-white/60">{section.description}</p>
                </motion.div>

                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.1 } }
                  }}
                  className="grid grid-cols-2 md:grid-cols-4 gap-4"
                >
                  {sectionImages.map((image, index) => (
                    <motion.div
                      key={image.id}
                      variants={fadeInUp}
                      className={`gallery-item rounded-xl overflow-hidden ${
                        index === 0 ? "col-span-2 row-span-2" : ""
                      }`}
                      data-testid={`gallery-image-${image.id}`}
                    >
                      <div className={`relative ${index === 0 ? 'aspect-square' : 'aspect-[4/3]'} bg-dream-bg`}>
                        <img
                          src={image.url}
                          alt={image.alt}
                          className={`w-full h-full ${section.id === 'photography' ? 'object-contain' : 'object-cover'}`}
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-end">
                          <div className="p-4">
                            <p className="text-white font-medium text-sm">{image.alt}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </section>
          );
        })
      )}

      {/* Note */}
      <section className="py-16 bg-dream-surface">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <p className="text-white/60">
              Want to see more? Visit us for a studio tour or follow us on social media 
              for the latest updates and behind-the-scenes content.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
